import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './VergilPage.css';

const vergilSkillTags = [
  { name: 'DARK SLAYER', color: '#1e90ff' },
  { name: 'YAMATO', color: '#4169e1' },
  { name: 'JUDGEMENT CUT', color: '#1e90ff' },
  { name: 'RAPID SLASH', color: '#4169e1' },
  { name: 'SUMMONED SWORDS', color: '#1e90ff' },
  { name: 'DOPPELGANGER', color: '#4169e1' },
  { name: 'CONCENTRATION', color: '#1e90ff' },
  { name: 'SIN DEVIL TRIGGER', color: '#4169e1' }
];

function VergilPage() {
  const [isDevilTrigger, setIsDevilTrigger] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const toggleForm = () => {
    setIsDevilTrigger(!isDevilTrigger);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
        delayChildren: 0.3
      }
    }
  };

  const tagVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { type: 'spring', stiffness: 300 }
    }
  };

  return (
    <div className="vergil-page">
      {/* Hero Section */}
      <section className="vergil-hero">
        {/* Background with Vergil in the back */}
        <div className="vergil-bg">
          <img 
            src="/images/vergil/vergil-back.png" 
            alt="Vergil Background" 
            className="vergil-bg-image"
          />
          <div className="vergil-bg-overlay"></div>
        </div>

        {/* Animated Background Elements */}
        <div className="vergil-bg-effects">
          <motion.div 
            className="vergil-energy-wave wave-1"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.1, 0.3]
            }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div 
            className="vergil-energy-wave wave-2"
            animate={{
              scale: [1, 1.8, 1],
              opacity: [0.2, 0.05, 0.2]
            }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
          
          {/* Floating particles - blue */}
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="vergil-floating-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -80, 0],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>

        <div className="vergil-container">
          {/* Section Header */}
          <motion.div 
            className="vergil-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="vergil-section-tag">THE ALPHA AND THE OMEGA</span>
            <h2 className="vergil-section-title">VERGIL</h2>
            <p className="vergil-section-subtitle">Son of Sparda • Dante's Twin Brother • The Dark Slayer</p>
          </motion.div>

          <div className="vergil-content-grid">
            {/* Left Content */}
            <motion.div 
              className="vergil-info"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div 
                className="vergil-badge"
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(30, 144, 255, 0.6)' }}
              >
                <motion.span 
                  className="badge-icon visual-icon lightning-icon"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
                >
                </motion.span>
                <span>LEGENDARY DARK SLAYER</span>
              </motion.div>

              <p className="vergil-description">
                I NEED MORE POWER.<br />
                FOOLISHNESS, DANTE. FOOLISHNESS.
              </p>

              {/* Form Toggle */}
              <div className="vergil-form-toggle">
                <button 
                  className={`vergil-toggle-btn ${!isDevilTrigger ? 'active' : ''}`}
                  onClick={() => setIsDevilTrigger(false)}
                >
                  HUMAN
                </button>
                <button 
                  className={`vergil-toggle-btn ${isDevilTrigger ? 'active' : ''}`}
                  onClick={() => setIsDevilTrigger(true)}
                >
                  SIN DEVIL TRIGGER
                </button>
              </div>

              {/* Stats */}
              <motion.div className="vergil-stats">
                <motion.div 
                  className="vergil-stat"
                  whileHover={{ scale: 1.1, y: -5 }}
                >
                  <motion.span 
                    className="stat-value"
                    animate={{ 
                      textShadow: [
                        '0 0 10px rgba(30, 144, 255, 0.5)',
                        '0 0 20px rgba(30, 144, 255, 1)',
                        '0 0 10px rgba(30, 144, 255, 0.5)'
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {isDevilTrigger ? '∞' : 'SSS'}
                  </motion.span>
                  <span className="stat-label">MOTIVATION</span>
                </motion.div>
                <motion.div 
                  className="vergil-stat"
                  whileHover={{ scale: 1.1, y: -5 }}
                >
                  <span className="stat-value">{isDevilTrigger ? '999+' : '100%'}</span>
                  <span className="stat-label">POWER</span>
                </motion.div>
                <motion.div 
                  className="vergil-stat"
                  whileHover={{ scale: 1.1, y: -5 }}
                >
                  <span className="stat-value">{isDevilTrigger ? 'MAX' : 'HIGH'}</span>
                  <span className="stat-label">CONCENTRATION</span>
                </motion.div>
              </motion.div>

              {/* Devil Trigger Button */}
              <motion.button 
                className="vergil-trigger-btn"
                onClick={toggleForm}
                whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(30, 144, 255, 0.8)' }}
                whileTap={{ scale: 0.95 }}
                animate={isDevilTrigger ? {
                  boxShadow: ['0 0 20px rgba(30, 144, 255, 0.5)', '0 0 40px rgba(30, 144, 255, 0.8)', '0 0 20px rgba(30, 144, 255, 0.5)']
                } : {}}
                transition={{ duration: 1, repeat: isDevilTrigger ? Infinity : 0 }}
              >
                
                <span>{isDevilTrigger ? 'DEACTIVATE' : 'SIN DEVIL TRIGGER'}</span>
              </motion.button>
            </motion.div>

            {/* Center - Character */}
            <div className="vergil-character-center">
              <motion.div 
                className="vergil-character-glow"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              
              <AnimatePresence mode="wait">
                <motion.div
                  key={isDevilTrigger ? 'devil' : 'human'}
                  className="vergil-character-image"
                  initial={{ opacity: 0, scale: 0.8, rotateY: 90 }}
                  animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                  exit={{ opacity: 0, scale: 0.8, rotateY: -90 }}
                  transition={{ duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <img 
                    src={isDevilTrigger 
                      ? '/images/vergil/vergil-devil-trigger.png' 
                      : '/images/vergil/ver.png'
                    } 
                    alt={isDevilTrigger ? 'Vergil Sin Devil Trigger' : 'Vergil'}
                    className="vergil-img"
                  />
                  
                  {/* Energy rings */}
                  <motion.div
                    className="vergil-energy-ring ring-1"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                  />
                  <motion.div
                    className="vergil-energy-ring ring-2"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
                  />
                </motion.div>
              </AnimatePresence>

              {/* Center Title */}
              <h1 className="vergil-center-title">
                <span className="title-line">DARK</span>
                <span className="title-line">SLAYER</span>
              </h1>

              {/* Form Label */}
              <motion.div 
                className="vergil-form-label"
                animate={{ opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {isDevilTrigger ? 'SIN DEVIL TRIGGER' : 'HUMAN FORM'}
              </motion.div>
            </div>

            {/* Right Content - Tags */}
            <motion.div 
              className="vergil-tags"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <motion.div className="vergil-trusted-badge">
                <div className="trusted-avatars">
                  <div className="avatar"></div>
                  <div className="avatar"></div>
                  <div className="avatar"></div>
                </div>
                <div className="trusted-text">
                  <span className="trusted-title">FEARED BY ALL DEMONS</span>
                  <span className="trusted-sub">— THE SON OF SPARDA</span>
                </div>
              </motion.div>

              <motion.div 
                className="vergil-skill-tags"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {vergilSkillTags.map((tag, index) => (
                  <motion.span 
                    key={index} 
                    className="vergil-skill-tag"
                    variants={tagVariants}
                    whileHover={{ 
                      scale: 1.15, 
                      backgroundColor: tag.color,
                      color: '#fff',
                      boxShadow: `0 0 20px ${tag.color}`
                    }}
                    whileTap={{ scale: 0.95 }}
                    style={{ borderColor: tag.color }}
                  >
                    {tag.name}
                  </motion.span>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Decorative sword slashes */}
        <div className="vergil-decoration">
          <motion.div 
            className="sword-slash slash-1"
            animate={{ opacity: [0, 1, 0], scaleX: [0, 1, 0] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3 }}
          />
          <motion.div 
            className="sword-slash slash-2"
            animate={{ opacity: [0, 1, 0], scaleX: [0, 1, 0] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3, delay: 0.1 }}
          />
          <motion.div 
            className="sword-slash slash-3"
            animate={{ opacity: [0, 1, 0], scaleX: [0, 1, 0] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 3, delay: 0.2 }}
          />
        </div>
      </section>

      {/* Weapon Section - Yamato */}
      <section className="vergil-weapon-section" id="weapons">
        <div className="weapon-container">
          <motion.div 
            className="weapon-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="weapon-tag">LEGENDARY DEVIL ARM</span>
            <h2 className="weapon-title">YAMATO</h2>
            <p className="weapon-subtitle">The Blade That Cuts Through Dimensions</p>
          </motion.div>

          <div className="weapon-content">
            <motion.div 
              className="weapon-info"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="weapon-name">O Katana of Sparda</h3>
              <p className="weapon-description">
                Yamato is a legendary katana once wielded by the Dark Knight Sparda. 
                This blade possesses the power to cut through anything, including the 
                very fabric of space itself. In Vergil's hands, it becomes an extension 
                of his pursuit for power.
              </p>
              
              <div className="weapon-stats">
                <div className="weapon-stat">
                  <span className="stat-icon visual-icon lightning-icon"></span>
                  <div className="stat-info">
                    <span className="stat-name">ATTACK POWER</span>
                    <div className="stat-bar">
                      <motion.div 
                        className="stat-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: '95%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                </div>
                <div className="weapon-stat">
                  <span className="stat-icon visual-icon speed-icon"></span>
                  <div className="stat-info">
                    <span className="stat-name">SPEED</span>
                    <div className="stat-bar">
                      <motion.div 
                        className="stat-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: '100%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.4 }}
                      />
                    </div>
                  </div>
                </div>
                <div className="weapon-stat">
                  <span className="stat-icon visual-icon precision-icon"></span>
                  <div className="stat-info">
                    <span className="stat-name">PRECISION</span>
                    <div className="stat-bar">
                      <motion.div 
                        className="stat-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: '100%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.5 }}
                      />
                    </div>
                  </div>
                </div>
                <div className="weapon-stat">
                  <span className="stat-icon visual-icon demonic-icon"></span>
                  <div className="stat-info">
                    <span className="stat-name">DEMONIC POWER</span>
                    <div className="stat-bar">
                      <motion.div 
                        className="stat-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: '90%' }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.6 }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="weapon-abilities">
                <h4>SIGNATURE MOVES</h4>
                <div className="ability-list">
                  <motion.div 
                    className="ability"
                    whileHover={{ scale: 1.05, x: 10 }}
                  >
                    <span className="ability-icon"></span>
                    <div className="ability-info">
                      <span className="ability-name">Judgement Cut</span>
                      <span className="ability-desc">Dimensional slash that cuts through space</span>
                    </div>
                  </motion.div>
                  <motion.div 
                    className="ability"
                    whileHover={{ scale: 1.05, x: 10 }}
                  >
                    <span className="ability-icon"></span>
                    <div className="ability-info">
                      <span className="ability-name">Rapid Slash</span>
                      <span className="ability-desc">Lightning-fast forward dash attack</span>
                    </div>
                  </motion.div>
                  <motion.div 
                    className="ability"
                    whileHover={{ scale: 1.05, x: 10 }}
                  >
                    <span className="ability-icon"></span>
                    <div className="ability-info">
                      <span className="ability-name">Judgement Cut End</span>
                      <span className="ability-desc">Ultimate technique - infinite dimensional cuts</span>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            <motion.div 
              className="weapon-display"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <motion.div 
                className="weapon-glow"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3]
                }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.img 
                src="/images/vergil/vergil-weapon.png" 
                alt="Yamato"
                className="weapon-image"
                animate={{
                  y: [0, -10, 0],
                  rotate: [0, 2, 0, -2, 0]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                whileHover={{ scale: 1.1, filter: 'drop-shadow(0 0 40px rgba(30, 144, 255, 1))' }}
              />
              
              {/* Energy effects around weapon */}
              <motion.div 
                className="weapon-energy"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery Section - Slider */}
      <section className="vergil-gallery-section" id="gallery">
        <div className="gallery-container">
          <div className="gallery-slider">
            <button 
              className="slider-arrow slider-prev" 
              onClick={() => setCurrentSlide(currentSlide === 0 ? 2 : currentSlide - 1)}
            >
              <span className="arrow-icon"></span>
            </button>
            
            <AnimatePresence mode="wait">
              <motion.div 
                key={currentSlide}
                className={`gallery-slide ${currentSlide === 0 ? 'glitch-effect' : ''}`}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3 }}
              >
                {currentSlide === 0 && (
                  <>
                    <img src="/images/vergil/ver.png" alt="Vergil Human Form" className="slide-image glitch-img" />
                    <div className="slide-label glitch-text">
                      <span>HUMAN FORM</span>
                    </div>
                  </>
                )}
                {currentSlide === 1 && (
                  <>
                    <img src="/images/vergil/vergil-devil-trigger.png" alt="Vergil Devil Trigger" className="slide-image" />
                    <div className="slide-label">
                      <span>SIN DEVIL TRIGGER</span>
                    </div>
                  </>
                )}
                {currentSlide === 2 && (
                  <>
                    <img src="/images/vergil/vergil-weapon.png" alt="Yamato" className="slide-image" />
                    <div className="slide-label">
                      <span>YAMATO</span>
                    </div>
                  </>
                )}
              </motion.div>
            </AnimatePresence>
            
            <button 
              className="slider-arrow slider-next" 
              onClick={() => setCurrentSlide(currentSlide === 2 ? 0 : currentSlide + 1)}
            >
              <span className="arrow-icon"></span>
            </button>
          </div>
          
          <div className="slider-dots">
            {[0, 1, 2].map((index) => (
              <button 
                key={index}
                className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="vergil-contact-section" id="contact">
        <div className="contact-container">
          <motion.div 
            className="contact-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="contact-tag">GET IN TOUCH</span>
            <h2 className="contact-title">CONTACT</h2>
            <p className="contact-subtitle">Send us a message</p>
          </motion.div>

          <motion.form 
            className="contact-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">NAME</label>
                <input type="text" id="name" name="name" placeholder="Your name" required />
              </div>
              <div className="form-group">
                <label htmlFor="email">EMAIL</label>
                <input type="email" id="email" name="email" placeholder="Your email" required />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="subject">SUBJECT</label>
              <input type="text" id="subject" name="subject" placeholder="Subject" required />
            </div>
            <div className="form-group">
              <label htmlFor="message">MESSAGE</label>
              <textarea id="message" name="message" rows="5" placeholder="Your message" required></textarea>
            </div>
            <motion.button 
              type="submit" 
              className="contact-submit-btn"
              whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(30, 144, 255, 0.8)' }}
              whileTap={{ scale: 0.95 }}
            >
              SEND MESSAGE
            </motion.button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}

export default VergilPage;
